// Compute financial statements from posted journals + opening balances.
// All money values in ZAR with 2 decimals (caller formats).

import { ACCOUNT_TYPES, indexCOA } from './coa';

// Filter journals into a date range [from, to] inclusive (ISO YYYY-MM-DD).
export function filterJournals(journals, from, to) {
  return (journals || []).filter((j) => {
    if (from && j.date < from) return false;
    if (to && j.date > to) return false;
    return true;
  });
}

// Aggregate per-account debit and credit movements across journals.
// Returns { [accountCode]: { dr, cr } }
export function accountMovements(journals) {
  const m = {};
  (journals || []).forEach((j) => {
    (j.lines || []).forEach((l) => {
      if (!m[l.accountCode]) m[l.accountCode] = { dr: 0, cr: 0 };
      m[l.accountCode].dr += Number(l.dr) || 0;
      m[l.accountCode].cr += Number(l.cr) || 0;
    });
  });
  return m;
}

// Given opening balances {[code]: {dr, cr}} and movements, return closing balances per account.
// Closing balance is expressed as a *signed* number on the account's normal side:
//   asset/expense/cogs (dr-normal) → +dr-side
//   liability/equity/revenue (cr-normal) → +cr-side
export function closingBalances(coa, opening, movements) {
  const byCode = indexCOA(coa);
  const out = {};
  const codes = new Set([...Object.keys(opening || {}), ...Object.keys(movements || {})]);
  codes.forEach((code) => {
    const acc = byCode[code];
    if (!acc) {
      // Unknown account — still record raw movements
      const o = (opening || {})[code] || { dr: 0, cr: 0 };
      const mv = (movements || {})[code] || { dr: 0, cr: 0 };
      out[code] = { dr: o.dr + mv.dr, cr: o.cr + mv.cr, balance: (o.dr + mv.dr) - (o.cr + mv.cr), unknown: true };
      return;
    }
    const typeMeta = ACCOUNT_TYPES[acc.type] || { side: 'dr' };
    const o = (opening || {})[code] || { dr: 0, cr: 0 };
    const mv = (movements || {})[code] || { dr: 0, cr: 0 };
    const totalDr = o.dr + mv.dr;
    const totalCr = o.cr + mv.cr;
    const balance = typeMeta.side === 'dr' ? totalDr - totalCr : totalCr - totalDr;
    out[code] = { dr: totalDr, cr: totalCr, balance, name: acc.name, type: acc.type };
  });
  return out;
}

// Bank reconciliation for one bank account over [from, to].
// Sums the captured movement on the bank account (dr = money in, cr = money out)
// and the book opening, so the caller can compare to the statement's actual
// brought-forward / closing balances and surface any missing transactions.
export function bankReconciliation(coa, opening, journals, bankCode, from, to) {
  const o = (opening || {})[bankCode] || { dr: 0, cr: 0 };
  const openingBankBalance = (Number(o.dr) || 0) - (Number(o.cr) || 0);
  let priorDr = 0, priorCr = 0, periodDr = 0, periodCr = 0, count = 0;
  (journals || []).forEach((j) => {
    const before = from && j.date < from;
    const inPeriod = (!from || j.date >= from) && (!to || j.date <= to);
    let touches = false;
    (j.lines || []).forEach((l) => {
      if (l.accountCode !== bankCode) return;
      touches = true;
      const dr = Number(l.dr) || 0, cr = Number(l.cr) || 0;
      if (before) { priorDr += dr; priorCr += cr; }
      else if (inPeriod) { periodDr += dr; periodCr += cr; }
    });
    if (inPeriod && touches) count++;
  });
  const openingPerBooks = openingBankBalance + (priorDr - priorCr); // bank balance at period start, per the books
  return {
    openingPerBooks,
    moneyIn: periodDr,   // debits to the bank asset = money received
    moneyOut: periodCr,  // credits to the bank asset = money paid out
    movement: periodDr - periodCr,
    count,
  };
}

// Build P&L numbers for a period (typically opening = empty for income/expense, which reset each year).
export function profitAndLoss(coa, journals) {
  const byCode = indexCOA(coa);
  const mv = accountMovements(journals);
  const sections = { revenue: [], cogs: [], expense: [], other_income: [], other_expense: [] };
  let rev = 0, cogs = 0, opex = 0, oi = 0, oe = 0;
  Object.keys(mv).forEach((code) => {
    const acc = byCode[code];
    if (!acc) return;
    const meta = ACCOUNT_TYPES[acc.type];
    if (!meta || meta.statement !== 'pl') return;
    const amount = meta.side === 'cr' ? mv[code].cr - mv[code].dr : mv[code].dr - mv[code].cr;
    if (amount === 0) return;
    const entry = { code, name: acc.name, amount };
    sections[acc.type].push(entry);
    if (acc.type === 'revenue') rev += amount;
    else if (acc.type === 'cogs') cogs += amount;
    else if (acc.type === 'expense') opex += amount;
    else if (acc.type === 'other_income') oi += amount;
    else if (acc.type === 'other_expense') oe += amount;
  });
  Object.keys(sections).forEach((k) => sections[k].sort((a, b) => a.code.localeCompare(b.code)));
  const gp = rev - cogs;
  const opInc = gp - opex;
  const np = opInc + oi - oe;
  return { rev, cogs, gp, opex, opInc, oi, oe, np, sections };
}

// Build balance-sheet groupings from closing balances.
export function balanceSheet(coa, opening, journals) {
  const mv = accountMovements(journals);
  const cb = closingBalances(coa, opening, mv);
  const assets = [], liabilities = [], equity = [];
  let tA = 0, tL = 0, tE = 0;
  Object.keys(cb).forEach((code) => {
    const row = cb[code];
    if (!row.type) return;
    const meta = ACCOUNT_TYPES[row.type];
    if (!meta || meta.statement !== 'bs') return;
    if (row.balance === 0) return;
    const entry = { code, name: row.name, amount: row.balance };
    if (row.type === 'asset') { assets.push(entry); tA += row.balance; }
    else if (row.type === 'liability') { liabilities.push(entry); tL += row.balance; }
    else if (row.type === 'equity') { equity.push(entry); tE += row.balance; }
  });
  // Current Year Earnings — derived from P&L, added to equity so BS balances.
  const pl = profitAndLoss(coa, journals);
  const cye = { code: '300030', name: 'Current Year Earnings (computed)', amount: pl.np };
  equity.push(cye);
  tE += pl.np;
  [assets, liabilities, equity].forEach((s) => s.sort((a, b) => a.code.localeCompare(b.code)));
  return {
    assets, liabilities, equity,
    totalAssets: tA, totalLiabilities: tL, totalEquity: tE,
    diff: tA - (tL + tE),
  };
}

// Trial balance — every account with non-zero closing dr/cr, totals at the bottom.
export function trialBalance(coa, opening, journals) {
  const mv = accountMovements(journals);
  const byCode = indexCOA(coa);
  const codes = new Set([...Object.keys(opening || {}), ...Object.keys(mv || {})]);
  const rows = [];
  let tdr = 0, tcr = 0;
  // A conventional trial balance lists revenue & expense accounts individually,
  // so it balances on its own — no computed Current Year Earnings line. (Profit
  // is summarised into CYE only on the balance sheet; see balanceSheet().)
  codes.forEach((code) => {
    const acc = byCode[code];
    const o = (opening || {})[code] || { dr: 0, cr: 0 };
    const m = mv[code] || { dr: 0, cr: 0 };
    const totalDr = o.dr + m.dr;
    const totalCr = o.cr + m.cr;
    const net = totalDr - totalCr;
    const meta = acc && ACCOUNT_TYPES[acc.type];
    // Show on the side of the net balance.
    const showDr = net > 0 ? net : 0;
    const showCr = net < 0 ? -net : 0;
    if (showDr === 0 && showCr === 0) return;
    rows.push({
      code,
      name: (acc && acc.name) || '(unknown)',
      type: acc ? acc.type : 'unknown',
      dr: showDr,
      cr: showCr,
    });
    tdr += showDr;
    tcr += showCr;
  });
  rows.sort((a, b) => a.code.localeCompare(b.code));
  return { rows, totalDr: tdr, totalCr: tcr, diff: tdr - tcr };
}

// General ledger — list of journal lines grouped by account, with running balance.
export function generalLedger(coa, opening, journals) {
  const byCode = indexCOA(coa);
  const groups = {};
  Object.keys(opening || {}).forEach((code) => {
    if (!groups[code]) groups[code] = [];
    const o = opening[code];
    if (o.dr || o.cr) groups[code].push({ date: '', desc: 'Opening Balance', dr: o.dr, cr: o.cr, narration: '' });
  });
  (journals || []).forEach((j) => {
    (j.lines || []).forEach((l) => {
      if (!groups[l.accountCode]) groups[l.accountCode] = [];
      groups[l.accountCode].push({
        date: j.date,
        desc: j.narration,
        dr: Number(l.dr) || 0,
        cr: Number(l.cr) || 0,
        narration: (j.sourceRow && j.sourceRow.description) || '',
      });
    });
  });
  const out = [];
  Object.keys(groups).sort().forEach((code) => {
    const acc = byCode[code];
    const meta = acc && ACCOUNT_TYPES[acc.type];
    const side = meta ? meta.side : 'dr';
    let running = 0;
    const lines = groups[code].map((row) => {
      const delta = side === 'dr' ? (row.dr - row.cr) : (row.cr - row.dr);
      running += delta;
      return { ...row, running };
    });
    out.push({ code, name: (acc && acc.name) || '(unknown)', type: acc ? acc.type : 'unknown', lines, closing: running });
  });
  return out;
}

// Group journals by month (YYYY-MM) — used by the BobCorp-style export.
export function monthsCovered(journals) {
  const set = new Set();
  (journals || []).forEach((j) => {
    if (j.date && j.date.length >= 7) set.add(j.date.slice(0, 7));
  });
  return Array.from(set).sort();
}

export function journalsForMonth(journals, ym) {
  return (journals || []).filter((j) => j.date && j.date.startsWith(ym));
}

// VAT201-style summary (estimate). Assumes posted amounts are VAT-inclusive and
// revenue is standard-rated. Input VAT is claimed only on deductible expense/cogs
// accounts (the COA `deductible` flag denies it for e.g. entertainment).
// other_income/other_expense (interest, refunds) are treated as exempt / out of
// scope. An estimate — the authoritative figure depends on actual tax invoices.
export function vatSummary(coa, journals, ratePct) {
  const rate = (Number(ratePct) || 15) / 100;
  const factor = rate / (1 + rate);
  const byCode = indexCOA(coa);
  const mv = accountMovements(journals);
  const output = [], input = [], excluded = [];
  let totalOutput = 0, totalInput = 0, standardSales = 0, inputPurchases = 0;
  Object.keys(mv).forEach((code) => {
    const acc = byCode[code]; if (!acc) return;
    const meta = ACCOUNT_TYPES[acc.type]; if (!meta || meta.statement !== 'pl') return;
    const amount = meta.side === 'cr' ? (mv[code].cr - mv[code].dr) : (mv[code].dr - mv[code].cr);
    if (!amount) return;
    if (acc.type === 'revenue') {
      const vo = amount * factor;
      output.push({ code, name: acc.name, amount, vat: vo });
      totalOutput += vo; standardSales += amount;
    } else if (acc.type === 'cogs' || acc.type === 'expense') {
      if (acc.deductible === false) {
        excluded.push({ code, name: acc.name, amount, reason: 'Input VAT denied' });
      } else {
        const vi = amount * factor;
        input.push({ code, name: acc.name, amount, vat: vi });
        totalInput += vi; inputPurchases += amount;
      }
    } else {
      excluded.push({ code, name: acc.name, amount, reason: 'Exempt / out of scope' });
    }
  });
  output.sort((a, b) => a.code.localeCompare(b.code));
  input.sort((a, b) => a.code.localeCompare(b.code));
  excluded.sort((a, b) => a.code.localeCompare(b.code));
  return {
    rate: (Number(ratePct) || 15), output, input, excluded,
    totalOutput, totalInput, net: totalOutput - totalInput,
    standardSales, inputPurchases,
  };
}
